import React, { useMemo, useState } from 'react';

import moment from 'moment';

import ApprovedTag from 'components/Tags/ApprovedTag';
import ExpirationTag from 'components/Tags/ExpirationTag';
import RejectedTag from 'components/Tags/RejectedTag';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { TravelType } from 'models/enums/travelType';
import { TravelRequested } from 'models/travelRequested';
import { copyContent } from 'utils/clipboard';
import { expirationHours, htmlT, splitDate } from 'utils/generic';
import { reservationCompanyImage, reservationImageByDestination } from 'utils/images';

import TravelTag, { TravelPolicies } from '../TravelTag';
import {
  Item,
  InfoContainer,
  Info,
  TravelCardHeader,
  Container,
  GuestIcon,
  AcceptButton,
  Travel,
  Code,
  IconCopyPaste,
  IconRappi,
  AirIcon,
  Divider,
  Footer,
  ToggleDetails,
  DropdownChevron,
  Details,
  TravelImage,
} from './styles';

interface Props {
  travel: TravelRequested;
  onClick: (id: string) => void;
  showExpirationTime?: boolean;
  showApprovedTag?: boolean;
  showRejectedTag?: boolean;
  testId?: string;
  onCancel: (id: string) => void;
  onChange?: (travel: TravelRequested) => void;
}

// TODO: code clean up
const CardFlight = ({
  travel,
  showExpirationTime,
  showApprovedTag,
  showRejectedTag,
  onClick,
  testId,
  onCancel,
  onChange,
}: Props) => {
  const { t } = useStoreTranslation();
  const [open, setOpen] = useState(false);
  const isExtraCharge = travel.type === TravelType.ExtraCharge;

  const formatDate = (date: string) => moment(splitDate(date)).format('ddd, DD MMM');

  /* const canExchange = useMemo(() => {
    if (travel.canChangeFlight === false) {
      return travel.canChangeFlight;
    }
    if (travel.type !== TravelType.Flight) return false;
    const TODAY = new Date();
    const dtDeparture = moment(travel.date);
    const dtReturn = moment(travel.returnDate);
    if (travel.travelAirStretch === 'Round-Trip') {
      if (dtReturn > moment(TODAY).add(24, 'hours')) {
        return true;
      }
    } else {
      if (dtDeparture > moment(TODAY).add(24, 'hours')) {
        return true;
      }
    }
    return false;
  }, [travel]); */

  const isOutOfPolicies = useMemo(() => travel.breakRulesMessage && travel.breakRulesMessage?.length > 0, [
    travel.breakRulesMessage,
  ]);
  const hasConfirmationCode = useMemo(
    () =>
      travel.airlineConfirmationCode && travel.airlineConfirmationCode?.toString() !== travel.itineraryId.toString(),
    [travel.airlineConfirmationCode, travel.itineraryId],
  );
  return (
    <Item testId={testId}>
      <Container>
        <TravelImage
          onClick={() => onClick(travel.id)}
          $image={reservationImageByDestination(travel.arrivalPoint)}
        ></TravelImage>
        <InfoContainer>
          <Travel>
            <Travel.Info onClick={() => onClick(travel.id)}>
              {travel.flightCity ? (
                <Travel.Destination>
                  {travel.flightCity}, {travel.countryDestination}
                </Travel.Destination>
              ) : (
                <Travel.Destination>{travel.arrivalPoint}</Travel.Destination>
              )}
              <Travel.Date>
                {!isExtraCharge && (
                  <>
                    {formatDate(travel.date)}
                    {((travel.type === TravelType.Flight && travel.travelAirStretch === 'Round-Trip') ||
                      travel.type !== TravelType.Flight) && <> - {formatDate(travel.returnDate)}</>}
                  </>
                )}
              </Travel.Date>
            </Travel.Info>
            <Travel.Status>
              {travel.travelStatus && <TravelTag status={travel.travelStatus} />}
              {/* <TravelTag status={TravelStatus.ExchangePending} /> */}
            </Travel.Status>
          </Travel>
          <Code
            onClick={() => {
              copyContent(travel.itineraryId);
            }}
          >
            <Code.Icon>
              <IconRappi />
            </Code.Icon>
            <Code.Item>
              <Code.Label>
                {htmlT(t, 'travelTravelCard.flight.rappiCode')} <strong>{travel.itineraryId}</strong>
              </Code.Label>
              <Code.Copy>
                <IconCopyPaste />
              </Code.Copy>
            </Code.Item>
          </Code>
          {travel.airlineCode && travel.airlineName && travel.airlineConfirmationCode && (
            <Code
              onClick={() => {
                if (hasConfirmationCode) copyContent(travel.airlineConfirmationCode);
              }}
            >
              <Code.Icon>
                <AirIcon src={reservationCompanyImage(travel.airlineCode)} />
              </Code.Icon>
              <Code.Item>
                <Code.Label>
                  {travel.airlineName}
                  {hasConfirmationCode && (
                    <>
                      <br />
                      {t('travelTravelCard.flight.airlineCode')} <strong>{travel.airlineConfirmationCode}</strong>
                    </>
                  )}
                </Code.Label>
                <Code.Copy>{hasConfirmationCode && <IconCopyPaste />}</Code.Copy>
              </Code.Item>
            </Code>
          )}
          {showExpirationTime || showApprovedTag || showRejectedTag ? (
            <TravelCardHeader>
              {showExpirationTime && <ExpirationTag showIcon time={expirationHours(travel.expirationDate)} />}
              {showApprovedTag && <ApprovedTag />}
              {showRejectedTag && <RejectedTag />}
            </TravelCardHeader>
          ) : null}
        </InfoContainer>
        {(travel.travelStatus === 'Pending' || travel.travelStatus === 'PreApproved') && (
          <InfoContainer>
            <br />
            <br />
            <AcceptButton onClick={() => onCancel(travel.id)}>{t('travelTravelCard.cancel')}</AcceptButton>
          </InfoContainer>
        )}
        {/*travel.type === TravelType.Flight && onChange && canExchange && (
          <InfoContainer>
            <br />
            <br />
            <AcceptButton onClick={() => onChange(travel)}>{t('travelTravelCard.change')}</AcceptButton>
          </InfoContainer>
        )*/}
        <Divider />
        <Footer>
          <Info>
            {isExtraCharge && travel.reason !== '' && <Info>{travel.reason}</Info>}
            <Info.User>{travel.name} </Info.User>
            <Info.Group>
              {travel.isGuest ? (
                <>
                  <GuestIcon color="currentColor" height={16} />
                  {t('requestCard.invited')}
                </>
              ) : (
                <>
                  <Info.GroupIcon />
                  {travel.userGroupName}
                </>
              )}
            </Info.Group>
          </Info>
          <Details>
            <Details.Bar onClick={() => setOpen(!open)}>
              {isOutOfPolicies ? (
                <TravelTag status={TravelPolicies.OutOfPolicies} />
              ) : (
                <TravelTag status={TravelPolicies.WithInPolicies} />
              )}
              <ToggleDetails $detailsOpen={open}>
                <DropdownChevron />
              </ToggleDetails>
            </Details.Bar>
            {open && (
              <>
                {isOutOfPolicies && <p>{travel.breakRulesMessage}</p>}
                <Details.Divider />
                <p>
                  {t('travelTravelCard.flight.requestedBy')}{' '}
                  {travel.requestedBy ? <>{travel.requestedBy}</> : <>{travel.name}</>}
                </p>
                {isOutOfPolicies && (
                  <>
                    <p>
                      {t('travelTravelCard.flight.travelJustify')} {travel.justifyForBreakingRules}
                    </p>
                    <p>
                      {t('travelTravelCard.flight.breakRulesReason')} {travel.breakRuleReasonName}
                    </p>
                  </>
                )}
              </>
            )}
          </Details>
        </Footer>
      </Container>
    </Item>
  );
};
export default CardFlight;
